import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Tabs,
  Input,
  Button,
  List,
  Tag,
  Empty,
  Spin,
  Modal,
  Select,
  Space,
  Switch,
  Card,
  Segmented,
  Dropdown,
  Tooltip,
  Table,
  InputNumber,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  FileTextOutlined,
  SolutionOutlined,
  WarningOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  DownloadOutlined,
  EyeOutlined,
  EditOutlined,
  BarChartOutlined,
  ReloadOutlined,
  SearchOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { RolesEnum } from "../../../services/rolesEnum";
import { isAuthorized } from "../../../services/authService";
import { httpService } from "../../../services/httpService.service";
import { hexToRgba } from "../../../services/helpFunctions/hexToRgba";
import MessagePopUp from "../../components/Messages/MessagePopUp";
import StatusPill from "../../components/StatusPill/StatusPill";
import Preview from "../planning/VisitBoard/TableView/modals/Preview";
import { resolveSiteVisitStatusName } from "../../../services/Enums/siteVisitStatusEnum";
import doneIcon from "../../../Assets/svg/done.svg";
import errorIcon from "../../../Assets/svg/exclamationMark.svg";
import {
  query as nlqQuery,
  generateSitRep,
  generateIPNarratives,
  detectRedFlagPatterns,
  exportDonorReport,
  getDrafts,
  commitDraft,
  discardDraft,
  AIDraftType,
  AIDraftStatus,
} from "../../../services/features/kapInsights.service";
import {
  getRedFlagPatterns,
  getPatternThresholds,
  upsertPatternThreshold,
} from "../../../services/features/redflag.service";
import "./AiAssistant.scss";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

const { TextArea } = Input;

// Track 2 AI hub. Everything is human-in-the-loop: drafts are reviewed and then
// committed or discarded — nothing is applied automatically.
const isPMorAdmin = () =>
  isAuthorized([RolesEnum.ProjectManager, RolesEnum.SystemAdmin]);

// Brand-aligned colours (all drawn from the existing platform palette).
const brand = "#aa1a5f";
const TYPE_COLOR = {
  QualityScore: "#aa1a5f",
  SmartTasks: "#0f7507",
  SitRep: "#325899",
  IPNarrative: "#964b00",
  DonorNarrative: "#045b46",
  PatternAlert: "#d4380d",
};
const STATUS_COLOR = {
  Draft: "#faad14",
  Committed: "#0f7507",
  Discarded: "#8c8c8c",
};

// Human-friendly labels for the draft type enum. The raw enum names run words
// together (PatternAlert, IPNarrative, DonorNarrative); spaced-out versions read
// far better in chips, filters and the review header.
const TYPE_LABEL = {
  QualityScore: "Quality Score",
  SmartTasks: "Smart Tasks",
  SitRep: "SitRep",
  IPNarrative: "IP Narrative",
  DonorNarrative: "Donor Narrative",
  PatternAlert: "Pattern Alert",
};
const typeLabel = (type) => {
  const name = AIDraftType[type];
  return TYPE_LABEL[name] || name || "Draft";
};
const SEVERITY_COLOR = {
  Critical: "#d4380d",
  Warning: "#faad14",
  Informational: "#8c8c8c",
};

// A rounded pill with a colour dot — matches the platform's status pills.
// Geometry is inlined (not just the .aiChip class) so it renders correctly even
// where the class scope doesn't reach — e.g. the review modal, which portals to
// document.body outside .aiAssistant.
const Chip = ({ color, children }) => (
  <span
    className="aiChip"
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "2px 10px",
      borderRadius: 16,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: "20px",
      whiteSpace: "nowrap",
      background: hexToRgba(color, 0.12),
      color,
    }}
  >
    <span
      className="aiDot"
      style={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
      }}
    />
    {children}
  </span>
);

// ---- Content helpers --------------------------------------------------------
const parseJson = (content) => {
  if (content == null) return null;
  if (typeof content === "object") return content;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const escapeHtml = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Inline markdown: bold, italic, code, links.
const inlineMd = (s) =>
  escapeHtml(s)
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*(?!\s)(.+?)\*/g, "$1<em>$2</em>")
    .replace(/`(.+?)`/g, "<code>$1</code>")
    .replace(
      /\[(.+?)\]\((.+?)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer">$1</a>'
    );

// A pragmatic markdown → HTML converter covering the subset the AI produces:
// headings, lists, tables, blockquotes, rules and paragraphs.
const mdToHtml = (md) => {
  if (md == null) return "";
  const lines = String(md).replace(/\r\n?/g, "\n").split("\n");
  const out = [];
  let i = 0;
  const isSep = (s) =>
    /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(s);
  const isUl = (s) => /^\s*[-*+]\s+/.test(s);
  const isOl = (s) => /^\s*\d+\.\s+/.test(s);

  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    if (h) {
      const lvl = h[1].length;
      out.push(`<h${lvl}>${inlineMd(h[2].trim())}</h${lvl}>`);
      i++;
      continue;
    }
    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      out.push("<hr/>");
      i++;
      continue;
    }
    if (line.includes("|") && i + 1 < lines.length && isSep(lines[i + 1])) {
      const row = (s) =>
        s
          .trim()
          .replace(/^\|/, "")
          .replace(/\|$/, "")
          .split("|")
          .map((c) => inlineMd(c.trim()));
      const headers = row(line);
      i += 2;
      const body = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) {
        body.push(row(lines[i]));
        i++;
      }
      out.push(
        `<table class="aiTable"><thead><tr>${headers
          .map((c) => `<th>${c}</th>`)
          .join("")}</tr></thead><tbody>${body
          .map(
            (r) => `<tr>${r.map((c) => `<td>${c}</td>`).join("")}</tr>`
          )
          .join("")}</tbody></table>`
      );
      continue;
    }
    if (isUl(line)) {
      const items = [];
      while (i < lines.length && isUl(lines[i])) {
        items.push(`<li>${inlineMd(lines[i].replace(/^\s*[-*+]\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ul>${items.join("")}</ul>`);
      continue;
    }
    if (isOl(line)) {
      const items = [];
      while (i < lines.length && isOl(lines[i])) {
        items.push(`<li>${inlineMd(lines[i].replace(/^\s*\d+\.\s+/, ""))}</li>`);
        i++;
      }
      out.push(`<ol>${items.join("")}</ol>`);
      continue;
    }
    if (/^\s*>\s?/.test(line)) {
      const items = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        items.push(inlineMd(lines[i].replace(/^\s*>\s?/, "")));
        i++;
      }
      out.push(`<blockquote>${items.join("<br/>")}</blockquote>`);
      continue;
    }
    const para = [];
    while (
      i < lines.length &&
      lines[i].trim() &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !isUl(lines[i]) &&
      !isOl(lines[i]) &&
      !/^\s*>\s?/.test(lines[i])
    ) {
      para.push(inlineMd(lines[i].trim()));
      i++;
    }
    out.push(`<p>${para.join("<br/>")}</p>`);
  }
  return out.join("\n");
};

const qualityToHtml = (data) => {
  const issues = (data.issues || [])
    .map(
      (x) =>
        `<tr><td>${escapeHtml(x.severity)}</td><td>${escapeHtml(
          x.message
        )}</td></tr>`
    )
    .join("");
  return (
    `<h1>Quality score: ${data.score ?? "—"} / 5</h1>` +
    `<p>${escapeHtml(data.summary)}</p>` +
    (issues
      ? `<h3>Issues</h3><table class="aiTable"><thead><tr><th>Severity</th><th>Detail</th></tr></thead><tbody>${issues}</tbody></table>`
      : "<p>No issues flagged.</p>")
  );
};

const tasksToHtml = (tasks) => {
  const rows = (Array.isArray(tasks) ? tasks : [])
    .map(
      (t) =>
        `<tr><td>${escapeHtml(t.name)}</td><td>${escapeHtml(
          t.type
        )}</td><td>${escapeHtml(t.description)}</td><td>${escapeHtml(
          t.deadline
        )}</td></tr>`
    )
    .join("");
  return `<h1>Proposed tasks</h1><table class="aiTable"><thead><tr><th>Name</th><th>Type</th><th>Description</th><th>Deadline</th></tr></thead><tbody>${rows}</tbody></table>`;
};

// Save an HTML fragment as a Word document (.doc opens natively in Word).
const exportWord = (html, filename) => {
  const doc =
    `<!DOCTYPE html><html xmlns:o="urn:schemas-microsoft-com:office:office" ` +
    `xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8">` +
    `<style>body{font-family:Calibri,Arial,sans-serif;color:#1f1f1f;font-size:11pt;line-height:1.5}` +
    `h1,h2,h3{color:#aa1a5f}table{border-collapse:collapse;width:100%}` +
    `td,th{border:1px solid #ccc;padding:6px 10px;text-align:left}` +
    `th{background:#fbeef5;color:#aa1a5f}</style></head><body>${html}</body></html>`;
  saveAs(
    new Blob(["﻿", doc], { type: "application/msword" }),
    `${filename}.doc`
  );
};

// Rasterise a DOM node and lay it out (paginated) into a PDF.
const exportPdf = async (node, filename) => {
  if (!node) return;
  const canvas = await html2canvas(node, {
    scale: 2,
    backgroundColor: "#ffffff",
    useCORS: true,
  });
  const pdf = new jsPDF("p", "pt", "a4");
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 32;
  const imgW = pageW - margin * 2;
  const imgH = (canvas.height * imgW) / canvas.width;
  const usable = pageH - margin * 2;
  const img = canvas.toDataURL("image/png");
  let heightLeft = imgH;
  let position = margin;
  pdf.addImage(img, "PNG", margin, position, imgW, imgH);
  heightLeft -= usable;
  while (heightLeft > 0) {
    pdf.addPage();
    position = margin - (imgH - heightLeft);
    pdf.addImage(img, "PNG", margin, position, imgW, imgH);
    heightLeft -= usable;
  }
  pdf.save(`${filename}.pdf`);
};

// ---- F-14 Natural-language querying -----------------------------------------
const AskTab = ({ notify }) => {
  const projectID = localStorage.getItem("currentProjectID");
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [previewId, setPreviewId] = useState(null);

  const isSiteVisit = (item) =>
    (item?.type || result?.entity || "").toLowerCase().includes("sitevisit");

  const ask = () => {
    if (!question.trim()) return;
    setLoading(true);
    nlqQuery(
      projectID,
      question,
      (res) => {
        setResult(res?.data || res || null);
        setLoading(false);
      },
      () => {
        notify(
          "danger",
          "Search failed",
          "Couldn't run that query. Please try again."
        );
        setLoading(false);
      }
    );
  };

  return (
    <div>
      <Space.Compact className="aiAskBar" style={{ width: "100%", maxWidth: 520 }}>
        <Input
          prefix={<SearchOutlined style={{ color: "#bbb" }} />}
          placeholder="Ask a question… e.g. visits in Kosovo with missing tracking last month"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onPressEnter={ask}
          allowClear
        />
        <Button className="addButtonTPM" onClick={ask} loading={loading}>
          Ask
        </Button>
      </Space.Compact>

      {result && (
        <div style={{ marginTop: 20 }}>
          <div style={{ color: "#707073", marginBottom: 8 }}>
            {result.interpretation}{" "}
            <Tag>{result.count ?? result.results?.length ?? 0} results</Tag>
            {result.entity && <Tag color="magenta">{result.entity}</Tag>}
          </div>
          <List
            bordered
            dataSource={result.results || []}
            locale={{ emptyText: <Empty description="No matching records" /> }}
            renderItem={(item) => {
              const canPreview = isSiteVisit(item) && item.id;
              return (
                <List.Item
                  actions={
                    canPreview
                      ? [
                          <Button
                            type="link"
                            key="preview"
                            onClick={() => setPreviewId(item.id)}
                          >
                            Preview
                          </Button>,
                        ]
                      : undefined
                  }
                >
                  <span
                    style={{ cursor: canPreview ? "pointer" : "default" }}
                    onClick={
                      canPreview ? () => setPreviewId(item.id) : undefined
                    }
                  >
                    {item.title || item.id}
                  </span>
                  <Space>
                    {item.type && <Tag>{item.type}</Tag>}
                    {item.status &&
                      (isSiteVisit(item) ? (
                        <StatusPill
                          text={resolveSiteVisitStatusName(item.status).text}
                          fill={resolveSiteVisitStatusName(item.status).fill}
                        />
                      ) : (
                        <Tag>{item.status}</Tag>
                      ))}
                  </Space>
                </List.Item>
              );
            }}
          />
        </div>
      )}

      <Preview
        visible={!!previewId}
        siteVisitCardId={previewId}
        onClose={() => setPreviewId(null)}
      />
    </div>
  );
};

// ---- Interactive draft body -------------------------------------------------
// Markdown drafts toggle between Preview / Edit; structured drafts (score,
// tasks) toggle between an at-a-glance Visual and the raw editable payload.
const DraftBody = ({ draft, view, editedContent, setEditedContent }) => {
  const typeName = AIDraftType[draft.type];

  if (typeName === "QualityScore") {
    if (view === "Raw") {
      return (
        <TextArea
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          autoSize={{ minRows: 12, maxRows: 30 }}
          style={{ fontFamily: "monospace" }}
        />
      );
    }
    const data = parseJson(editedContent) || parseJson(draft.content) || {};
    const score = Number(data.score ?? draft.score ?? 0);
    const counts = { Critical: 0, Warning: 0, Informational: 0 };
    (data.issues || []).forEach((it) => {
      if (counts[it.severity] != null) counts[it.severity] += 1;
    });
    return (
      <div className="aiFormatted">
        <div className="aiScoreWrap">
          <div className="aiScoreChart">
            <Doughnut
              data={{
                labels: ["Score", "Remaining"],
                datasets: [
                  {
                    data: [score, Math.max(0, 5 - score)],
                    backgroundColor: [brand, "#f0e6ee"],
                    borderWidth: 0,
                  },
                ],
              }}
              options={{
                cutout: "76%",
                plugins: { legend: { display: false }, tooltip: { enabled: false } },
              }}
            />
            <div className="aiScoreValue">
              <strong>{score || "—"}</strong>
              <span>out of 5</span>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <h3 style={{ marginTop: 0 }}>Summary</h3>
            <p>{data.summary || "No summary provided."}</p>
          </div>
        </div>

        <h3>Issues by severity</h3>
        {(data.issues || []).length ? (
          <>
            <div style={{ maxWidth: 460, height: 200 }}>
              <Bar
                data={{
                  labels: Object.keys(counts),
                  datasets: [
                    {
                      data: Object.values(counts),
                      backgroundColor: Object.keys(counts).map(
                        (k) => SEVERITY_COLOR[k]
                      ),
                      borderRadius: 6,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
                }}
              />
            </div>
            <List
              size="small"
              dataSource={data.issues || []}
              renderItem={(issue) => (
                <List.Item>
                  <Chip color={SEVERITY_COLOR[issue.severity] || "#8c8c8c"}>
                    {issue.severity}
                  </Chip>
                  <span style={{ marginLeft: 10 }}>{issue.message}</span>
                </List.Item>
              )}
            />
          </>
        ) : (
          <p>No issues flagged.</p>
        )}
      </div>
    );
  }

  if (typeName === "SmartTasks") {
    if (view === "Raw") {
      return (
        <TextArea
          value={editedContent}
          onChange={(e) => setEditedContent(e.target.value)}
          autoSize={{ minRows: 12, maxRows: 30 }}
          style={{ fontFamily: "monospace" }}
        />
      );
    }
    const tasks = parseJson(editedContent) || parseJson(draft.content) || [];
    const list = Array.isArray(tasks) ? tasks : [];
    const byType = {};
    list.forEach((t) => {
      const k = t.type || "Other";
      byType[k] = (byType[k] || 0) + 1;
    });
    const typeKeys = Object.keys(byType);
    return (
      <div className="aiFormatted">
        <h3 style={{ marginTop: 0 }}>Proposed tasks ({list.length})</h3>
        {typeKeys.length > 1 && (
          <div style={{ maxWidth: 460, height: 200, marginBottom: 16 }}>
            <Bar
              data={{
                labels: typeKeys,
                datasets: [
                  {
                    data: typeKeys.map((k) => byType[k]),
                    backgroundColor: brand,
                    borderRadius: 6,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
              }}
            />
          </div>
        )}
        {list.length ? (
          list.map((t, idx) => (
            <div className="aiTaskCard" key={idx}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span className="aiTaskName">{t.name}</span>
                {t.type && <Chip color={TYPE_COLOR.SmartTasks}>{t.type}</Chip>}
              </div>
              <div className="aiTaskDesc">
                {t.description}
                {t.deadline ? ` · due ${t.deadline}` : ""}
              </div>
            </div>
          ))
        ) : (
          <p>No tasks proposed.</p>
        )}
      </div>
    );
  }

  // Markdown / text drafts.
  if (view === "Edit") {
    return (
      <TextArea
        value={editedContent}
        onChange={(e) => setEditedContent(e.target.value)}
        autoSize={{ minRows: 14, maxRows: 34 }}
        style={{ fontFamily: "monospace" }}
      />
    );
  }
  return (
    <div
      className="aiFormatted"
      dangerouslySetInnerHTML={{ __html: mdToHtml(editedContent) }}
    />
  );
};

// ---- Drafts inbox -----------------------------------------------------------
const MARKDOWN_TYPES = ["SitRep", "IPNarrative", "DonorNarrative", "PatternAlert"];

const DraftsTab = ({ notify }) => {
  const projectID = localStorage.getItem("currentProjectID");
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState(undefined);
  const [statusFilter, setStatusFilter] = useState(undefined); // no preselection
  const [active, setActive] = useState(null);
  const [editedContent, setEditedContent] = useState("");
  const [view, setView] = useState("Preview");
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  // Only PMs / system admins can act on a draft (edit + commit/discard).
  // Everyone else gets a read-only review: preview + close.
  const canEdit = isPMorAdmin();

  const load = useCallback(() => {
    setLoading(true);
    getDrafts(
      {
        projectId: projectID || undefined,
        type: typeFilter,
        status: statusFilter,
      },
      (res) => {
        const list = res?.data || res || [];
        setDrafts(Array.isArray(list) ? list : []);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [projectID, typeFilter, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const isMarkdown = active && MARKDOWN_TYPES.includes(AIDraftType[active.type]);
  const viewOptions = useMemo(() => {
    if (!active) return [];
    if (isMarkdown) {
      const opts = [{ label: "Preview", value: "Preview", icon: <EyeOutlined /> }];
      if (canEdit) opts.push({ label: "Edit", value: "Edit", icon: <EditOutlined /> });
      return opts;
    }
    const opts = [{ label: "Visual", value: "Visual", icon: <BarChartOutlined /> }];
    if (canEdit) opts.push({ label: "Raw", value: "Raw", icon: <EditOutlined /> });
    return opts;
  }, [active, isMarkdown, canEdit]);

  const open = (draft) => {
    setActive(draft);
    setView(MARKDOWN_TYPES.includes(AIDraftType[draft.type]) ? "Preview" : "Visual");
    setEditedContent(
      typeof draft.content === "string"
        ? draft.content
        : JSON.stringify(draft.content, null, 2)
    );
  };

  const doCommit = () => {
    setBusy(true);
    commitDraft(
      active.id,
      editedContent,
      () => {
        notify("success", "Draft committed", "The draft has been committed.");
        setBusy(false);
        setActive(null);
        load();
      },
      () => {
        notify("danger", "Commit failed", "Couldn't commit draft.");
        setBusy(false);
      }
    );
  };

  const doDiscard = () => {
    setBusy(true);
    discardDraft(
      active.id,
      () => {
        notify("success", "Draft discarded", "The draft has been discarded.");
        setBusy(false);
        setActive(null);
        load();
      },
      () => {
        notify("danger", "Discard failed", "Couldn't discard draft.");
        setBusy(false);
      }
    );
  };

  const exportName = active
    ? `${(active.title || AIDraftType[active.type] || "draft")
        .replace(/[^\w-]+/g, "-")
        .toLowerCase()}`
    : "draft";

  const buildExportHtml = () => {
    const typeName = active && AIDraftType[active.type];
    if (typeName === "QualityScore")
      return qualityToHtml(parseJson(editedContent) || {});
    if (typeName === "SmartTasks")
      return tasksToHtml(parseJson(editedContent) || []);
    return mdToHtml(editedContent);
  };

  const downloadItems = [
    {
      key: "pdf",
      icon: <FilePdfOutlined />,
      label: "Download as PDF",
      onClick: () => exportPdf(bodyRef.current, exportName),
    },
    {
      key: "word",
      icon: <FileWordOutlined />,
      label: "Download as Word",
      onClick: () => exportWord(buildExportHtml(), exportName),
    },
  ];

  return (
    <div>
      <div className="aiToolbar">
        <Select
          allowClear
          placeholder="All types"
          style={{ width: 190 }}
          value={typeFilter}
          onChange={setTypeFilter}
          options={Object.entries(AIDraftType).map(([value, label]) => ({
            value: Number(value),
            label: TYPE_LABEL[label] || label,
          }))}
        />
        <Select
          allowClear
          placeholder="All statuses"
          style={{ width: 170 }}
          value={statusFilter}
          onChange={setStatusFilter}
          options={Object.entries(AIDraftStatus).map(([value, label]) => ({
            value: Number(value),
            label,
          }))}
        />
        <Tooltip title="Refresh">
          <Button className="iconButtonTPM" icon={<ReloadOutlined />} onClick={load} />
        </Tooltip>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : drafts.length === 0 ? (
        <Empty description="No drafts yet — generate one from the Generate tab." />
      ) : (
        <div className="aiDraftGrid">
          {drafts.map((d) => {
            const typeName = AIDraftType[d.type];
            const statusName = AIDraftStatus[d.status];
            return (
              <div className="aiDraftCard" key={d.id}>
                <div className="aiChips">
                  <Chip color={TYPE_COLOR[typeName] || brand}>
                    {typeLabel(d.type)}
                  </Chip>
                  <Chip color={STATUS_COLOR[statusName] || "#8c8c8c"}>
                    {statusName}
                  </Chip>
                </div>
                <p className="aiDraftTitle">{d.title || typeLabel(d.type)}</p>
                {d.entityType && (
                  <span className="aiDraftMeta">{d.entityType}</span>
                )}
                <div className="aiDraftFooter">
                  <Button
                    type="text"
                    style={{ color: brand, fontWeight: 600 }}
                    onClick={() => open(d)}
                  >
                    Review <RightOutlined />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={!!active}
        rootClassName="aiReviewModal"
        title={
          active ? (
            <div className="aiModalHead">
              <div className="aiModalHeadTitle">
                {active.title || typeLabel(active.type)}
              </div>
              <div className="aiModalHeadChips">
                <Chip color={TYPE_COLOR[AIDraftType[active.type]] || brand}>
                  {typeLabel(active.type)}
                </Chip>
                <Chip
                  color={STATUS_COLOR[AIDraftStatus[active.status]] || "#8c8c8c"}
                >
                  {AIDraftStatus[active.status]}
                </Chip>
              </div>
            </div>
          ) : (
            ""
          )
        }
        onCancel={() => setActive(null)}
        width={820}
        footer={
          active && AIDraftStatus[active.status] === "Draft" && canEdit
            ? [
                <Button
                  key="discard"
                  className="aiDiscardBtn"
                  onClick={doDiscard}
                  loading={busy}
                >
                  Discard
                </Button>,
                <Button
                  key="commit"
                  className="aiPrimaryBtn"
                  onClick={doCommit}
                  loading={busy}
                >
                  Commit
                </Button>,
              ]
            : [
                <Button
                  key="close"
                  className="aiGhostBtn"
                  onClick={() => setActive(null)}
                >
                  Close
                </Button>,
              ]
        }
      >
        {active && (
          <>
            <div className="aiReviewToolbar">
              {viewOptions.length > 1 ? (
                <Segmented
                  size="large"
                  value={view}
                  onChange={setView}
                  options={viewOptions}
                />
              ) : (
                <span />
              )}
              <Dropdown
                menu={{ items: downloadItems }}
                trigger={["click"]}
                placement="bottomRight"
              >
                <Button className="aiDownloadBtn" icon={<DownloadOutlined />}>
                  Download
                </Button>
              </Dropdown>
            </div>
            <div className="aiReviewBody" ref={bodyRef}>
              <DraftBody
                draft={active}
                view={view}
                editedContent={editedContent}
                setEditedContent={setEditedContent}
              />
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

// ---- Generate (SitRep F-15, patterns F-18, donor export F-19) ---------------
const GenerateTab = ({ notify, goToDrafts }) => {
  const projectID = localStorage.getItem("currentProjectID");
  const [busy, setBusy] = useState("");
  const [ipOptions, setIpOptions] = useState([]);
  const [selectedIps, setSelectedIps] = useState([]);
  const [donorFacing, setDonorFacing] = useState(false);

  useEffect(() => {
    if (!projectID) return;
    httpService.get(
      `/api/IPPlan/getAllIPPlans?ProjectId=${projectID}&pageSize=1000&pageNumber=1`,
      (res) => {
        const list = res?.data || [];
        setIpOptions(
          (Array.isArray(list) ? list : []).map((ip) => ({
            value: ip.id,
            label:
              ip?.implementingPartner?.name ||
              ip?.ipPlanCode ||
              ip?.identifier ||
              "IP plan",
          }))
        );
      },
      () => {}
    );
  }, [projectID]);

  const afterGenerate = (label) => {
    notify("success", label, "Draft ready — opening the AI drafts inbox.");
    setBusy("");
    goToDrafts && goToDrafts();
  };

  const runIPNarratives = () => {
    if (!selectedIps.length) {
      notify("danger", "Nothing selected", "Select at least one IP plan.");
      return;
    }
    setBusy("narratives");
    generateIPNarratives(
      selectedIps,
      donorFacing,
      () => afterGenerate("Narratives generated"),
      () => {
        notify("danger", "Generation failed", "Couldn't generate narratives.");
        setBusy("");
      }
    );
  };

  const runSitRep = () => {
    setBusy("sitrep");
    generateSitRep(
      { projectId: projectID },
      () => afterGenerate("SitRep generated"),
      () => {
        notify("danger", "Generation failed", "Couldn't generate SitRep.");
        setBusy("");
      }
    );
  };

  const runPatterns = () => {
    setBusy("patterns");
    detectRedFlagPatterns(
      () => afterGenerate("Pattern detection run"),
      () => {
        notify("danger", "Detection failed", "Couldn't run pattern detection.");
        setBusy("");
      }
    );
  };

  const runDonorExport = async () => {
    setBusy("donor");
    try {
      const res = await exportDonorReport(projectID);
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "donor-report.xlsx");
      notify("success", "Donor report exported", "The .xlsx has been downloaded.");
    } catch {
      notify("danger", "Export failed", "Couldn't export donor report.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="aiGenerateGrid">
      {/* IP / donor narratives */}
      <Card className="aiCard" bordered={false}>
        <div
          className="aiCardIcon"
          style={{ background: hexToRgba(TYPE_COLOR.IPNarrative, 0.12), color: TYPE_COLOR.IPNarrative }}
        >
          <SolutionOutlined />
        </div>
        <p className="aiCardTitle">IP / donor narratives</p>
        <p className="aiCardDesc">
          Draft a structured narrative for one or more IP plans. Toggle
          donor-facing wording, then review each draft.
        </p>
        <div className="aiCardControls">
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            placeholder="Select IP plans"
            value={selectedIps}
            onChange={setSelectedIps}
            options={ipOptions}
            optionFilterProp="label"
            maxTagCount="responsive"
          />
          <Space>
            <Switch checked={donorFacing} onChange={setDonorFacing} />
            <span>Donor-facing wording</span>
          </Space>
        </div>
        <div className="aiCardFooter">
          <Button
            block
            className="addButtonTPM"
            loading={busy === "narratives"}
            onClick={runIPNarratives}
          >
            Generate narrative drafts
          </Button>
        </div>
      </Card>

      {/* SitRep */}
      <Card className="aiCard" bordered={false}>
        <div
          className="aiCardIcon"
          style={{ background: hexToRgba(TYPE_COLOR.SitRep, 0.12), color: TYPE_COLOR.SitRep }}
        >
          <FileTextOutlined />
        </div>
        <p className="aiCardTitle">Situation report (SitRep)</p>
        <p className="aiCardDesc">
          Draft a SitRep from completed visit data for this project. Review and
          edit it before exporting to PDF or Word.
        </p>
        <div className="aiCardFooter">
          <Button
            block
            className="addButtonTPM"
            loading={busy === "sitrep"}
            onClick={runSitRep}
          >
            Generate SitRep draft
          </Button>
        </div>
      </Card>

      {/* Pattern detection */}
      <Card className="aiCard" bordered={false}>
        <div
          className="aiCardIcon"
          style={{ background: hexToRgba(TYPE_COLOR.PatternAlert, 0.12), color: TYPE_COLOR.PatternAlert }}
        >
          <WarningOutlined />
        </div>
        <p className="aiCardTitle">Red-flag pattern detection</p>
        <p className="aiCardDesc">
          Scan active flags for recurring patterns across IPs, clusters and
          regions. Also runs automatically each day.
        </p>
        <div className="aiCardFooter">
          <Button
            block
            className="inviteButtonTPM"
            loading={busy === "patterns"}
            onClick={runPatterns}
          >
            Detect patterns now
          </Button>
        </div>
      </Card>

      {/* Donor export */}
      <Card className="aiCard" bordered={false}>
        <div
          className="aiCardIcon"
          style={{ background: hexToRgba(TYPE_COLOR.DonorNarrative, 0.12), color: TYPE_COLOR.DonorNarrative }}
        >
          <FileExcelOutlined />
        </div>
        <p className="aiCardTitle">Donor report export</p>
        <p className="aiCardDesc">
          Export project data pre-formatted to the donor template as an Excel
          workbook (.xlsx).
        </p>
        <div className="aiCardFooter">
          <Button
            block
            className="inviteButtonTPM"
            loading={busy === "donor"}
            onClick={runDonorExport}
          >
            Export donor report
          </Button>
        </div>
      </Card>
    </div>
  );
};

// ---- Patterns: red-flag pattern detection + configurable thresholds ---------
// A pattern is a cluster of same-type red flags that crosses a configured
// threshold within a rolling window. Grouping is by issue type + cluster +
// implementing partner, so PMs can see which IP a concentration belongs to and
// filter down to one IP. They can also tune the thresholds (min count / window)
// per issue type.
const PatternsTab = ({ notify }) => {
  const projectID = localStorage.getItem("currentProjectID");
  const navigate = useNavigate();
  const [patterns, setPatterns] = useState([]);
  const [loadingPatterns, setLoadingPatterns] = useState(true);
  const [running, setRunning] = useState(false);
  const [thresholds, setThresholds] = useState([]);
  const [edits, setEdits] = useState({});
  const [loadingThresholds, setLoadingThresholds] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [implementingPartners, setImplementingPartners] = useState([]);
  const [ipFilter, setIpFilter] = useState(undefined);

  const loadPatterns = useCallback(() => {
    setLoadingPatterns(true);
    getRedFlagPatterns(
      { projectId: projectID, implementingPartnerId: ipFilter },
      (res) => {
        const list = res?.data || res || [];
        setPatterns(Array.isArray(list) ? list : []);
        setLoadingPatterns(false);
      },
      () => setLoadingPatterns(false)
    );
  }, [projectID, ipFilter]);

  // IP options for the filter — same endpoint/shape the visit-board filters use.
  useEffect(() => {
    const contractId = localStorage.getItem("currentContractId");
    httpService.get(
      `/api/ImplementingPartner/getAllImplementingPartners?contractId=${contractId}&IgnorePagination=true`,
      (res) => setImplementingPartners(res?.data || []),
      () => setImplementingPartners([])
    );
  }, []);

  const loadThresholds = useCallback(() => {
    setLoadingThresholds(true);
    getPatternThresholds(
      (res) => {
        const rows = res?.data || res || [];
        const list = Array.isArray(rows) ? rows : [];
        setThresholds(list);
        const e = {};
        list.forEach((r) => {
          e[r.id ?? r.issueType] = {
            minCount: r.minCount,
            windowDays: r.windowDays,
          };
        });
        setEdits(e);
        setLoadingThresholds(false);
      },
      () => setLoadingThresholds(false)
    );
  }, []);

  // Kept separate so changing the IP filter re-reads patterns only — reloading
  // the thresholds would throw away any unsaved edits in the table below.
  useEffect(() => {
    loadPatterns();
  }, [loadPatterns]);

  useEffect(() => {
    loadThresholds();
  }, [loadThresholds]);

  const runDetection = () => {
    setRunning(true);
    detectRedFlagPatterns(
      () => {
        setRunning(false);
        notify(
          "success",
          "Detection run",
          "Pattern detection completed. Any new patterns are shown below."
        );
        loadPatterns();
      },
      () => {
        setRunning(false);
        notify("danger", "Detection failed", "Couldn't run pattern detection.");
      }
    );
  };

  const setEdit = (key, field, value) =>
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));

  const saveThreshold = (row) => {
    const key = row.id ?? row.issueType;
    const e = edits[key] || {};
    setSavingKey(key);
    upsertPatternThreshold(
      {
        issueType: row.issueType,
        minCount: e.minCount,
        windowDays: e.windowDays,
      },
      () => {
        setSavingKey(null);
        notify(
          "success",
          "Threshold saved",
          `${row.issueTypeLabel || "Issue type"} threshold updated.`
        );
        loadThresholds();
      },
      () => {
        setSavingKey(null);
        notify("danger", "Save failed", "Couldn't save the threshold.");
      }
    );
  };

  const thresholdColumns = [
    {
      title: "Issue type",
      dataIndex: "issueTypeLabel",
      key: "issueTypeLabel",
      render: (label) => <strong>{label || "—"}</strong>,
    },
    {
      title: "Min count",
      key: "minCount",
      width: 140,
      render: (_, row) => {
        const key = row.id ?? row.issueType;
        return (
          <InputNumber
            min={1}
            value={edits[key]?.minCount}
            onChange={(v) => setEdit(key, "minCount", v)}
          />
        );
      },
    },
    {
      title: "Window (days)",
      key: "windowDays",
      width: 150,
      render: (_, row) => {
        const key = row.id ?? row.issueType;
        return (
          <InputNumber
            min={1}
            value={edits[key]?.windowDays}
            onChange={(v) => setEdit(key, "windowDays", v)}
          />
        );
      },
    },
    {
      title: "",
      key: "action",
      width: 110,
      render: (_, row) => {
        const key = row.id ?? row.issueType;
        return (
          <Button
            size="small"
            className="inviteButtonTPM"
            loading={savingKey === key}
            onClick={() => saveThreshold(row)}
          >
            Save
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <div className="aiToolbar">
        <span className="aiSectionTitle">Detected patterns</span>
        <Space wrap>
          {/* Grouping now includes the implementing partner, so patterns can be
              narrowed to a single IP server-side. */}
          <Select
            allowClear
            showSearch
            placeholder="All implementing partners"
            style={{ minWidth: 240 }}
            value={ipFilter}
            onChange={setIpFilter}
            optionFilterProp="label"
            options={implementingPartners.map((ip) => ({
              value: ip.id,
              label: ip.name,
            }))}
          />
          <Button
            className="addButtonTPM"
            icon={<WarningOutlined />}
            loading={running}
            onClick={runDetection}
          >
            Run detection
          </Button>
          <Tooltip title="Reload patterns">
            <Button className="iconButtonTPM" icon={<ReloadOutlined />} onClick={loadPatterns} />
          </Tooltip>
        </Space>
      </div>

      {loadingPatterns ? (
        <div style={{ padding: 40, textAlign: "center" }}>
          <Spin />
        </div>
      ) : patterns.length === 0 ? (
        <Empty
          description={
            ipFilter
              ? "No patterns for this implementing partner — flag volumes are within the configured thresholds."
              : "No patterns detected — red-flag volumes are within the configured thresholds."
          }
        />
      ) : (
        <div className="aiGenerateGrid">
          {patterns.map((p, i) => {
            const breached = p.threshold != null && p.count >= p.threshold;
            const color = breached ? "#d4380d" : brand;
            return (
              <Card
                key={`${p.issueType}-${p.clusterId || "noCluster"}-${
                  p.implementingPartnerId || "noIp"
                }-${i}`}
                className="aiCard"
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <Chip color={color}>{p.issueTypeLabel || "Pattern"}</Chip>
                  {breached && <Tag color="error">Threshold exceeded</Tag>}
                </div>
                {/* Which IP the concentration belongs to — null when the group's
                    flags aren't attributable to a single partner. Same chip
                    vocabulary as the rest of the tab. */}
                <div style={{ marginTop: 10 }}>
                  <Tooltip title="Implementing partner">
                    <Chip color={p.implementingPartnerName ? "#325899" : "#707073"}>
                      {p.implementingPartnerName || "No single IP"}
                    </Chip>
                  </Tooltip>
                </div>
                <div className="aiPatternMetric" style={{ color }}>
                  {p.count}
                  {p.threshold != null && (
                    <span className="aiPatternMetricSub"> / {p.threshold}</span>
                  )}
                </div>
                <p className="aiPatternMeta">
                  flags in the last {p.windowDays} days
                  {p.region ? ` · ${p.region}` : ""}
                </p>
                {p.summary && <p className="aiCardDesc">{p.summary}</p>}
                <div className="aiCardFooter">
                  <Button
                    block
                    className="inviteButtonTPM"
                    icon={<RightOutlined />}
                    onClick={() => navigate("/project/flags?Flags")}
                  >
                    View {p.flagIds?.length || 0} flag
                    {(p.flagIds?.length || 0) === 1 ? "" : "s"}
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="aiToolbar" style={{ marginTop: 28 }}>
        <span className="aiSectionTitle">Threshold configuration</span>
      </div>
      <p className="aiIntro" style={{ marginBottom: 12 }}>
        A pattern is raised when at least the minimum number of same-type flags
        occur within the rolling window. Changes apply to the next detection run.
      </p>
      <Table
        rowKey={(r) => r.id ?? r.issueType}
        columns={thresholdColumns}
        dataSource={thresholds}
        loading={loadingThresholds}
        pagination={false}
        locale={{ emptyText: <Empty description="No thresholds configured yet" /> }}
        size="middle"
      />
    </div>
  );
};

const AiAssistant = () => {
  const canGenerate = isPMorAdmin();
  const [activeKey, setActiveKey] = useState(canGenerate ? "generate" : "drafts");
  const [msg, setMsg] = useState({
    trigger: false,
    type: "",
    icon: "",
    title: "",
    text: "",
  });
  const notify = (type, title, text) => {
    setMsg({
      trigger: true,
      type,
      icon: type === "success" ? doneIcon : errorIcon,
      title,
      text,
    });
    setTimeout(() => setMsg((m) => ({ ...m, trigger: false })), 3000);
  };

  // Order: Generate → AI drafts → Ask.
  const items = [];
  if (canGenerate) {
    items.push({
      key: "generate",
      label: "Generate",
      children: (
        <GenerateTab notify={notify} goToDrafts={() => setActiveKey("drafts")} />
      ),
    });
  }
  items.push({
    key: "drafts",
    label: "AI drafts",
    children: <DraftsTab notify={notify} />,
  });
  items.push({
    key: "ask",
    label: "Ask (search)",
    children: <AskTab notify={notify} />,
  });
  if (canGenerate) {
    items.push({
      key: "patterns",
      label: "Patterns",
      children: <PatternsTab notify={notify} />,
    });
  }

  return (
    <div className="page-content aiAssistant" style={{ padding: 24 }}>
      <MessagePopUp
        trigger={msg.trigger}
        type={msg.type}
        icon={msg.icon}
        messageTitle={msg.title}
        messageText={msg.text}
      />
      <p className="aiIntro">
        AI output is always a draft you review before anything is applied.
      </p>
      <Tabs
        type="card"
        className="card-tabs"
        activeKey={activeKey}
        onChange={setActiveKey}
        destroyInactiveTabPane
        items={items}
      />
    </div>
  );
};

export default AiAssistant;
